import { useState, useEffect } from "react";
import { Gift, Users, DollarSign, Loader2, Check, X, Plus, Fuel, Copy, ChevronDown, ChevronUp, Car, Phone, Mail, Clock, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import api from "../../utils/api";

const API = `/api`;

export function ReferralsSection() {
  const [referrals, setReferrals] = useState([]);
  const [stats, setStats] = useState({ total_codes: 0, active_codes: 0, total_referrals: 0, total_earned: 0, pending_payouts: 0 });
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [expandedReferral, setExpandedReferral] = useState(null);
  
  // Add referral form
  const [newReferral, setNewReferral] = useState({
    referrer_name: "",
    referrer_email: "",
    referrer_phone: "",
    referrer_vehicle: "",
    reward_amount: 100
  });
  const [submitting, setSubmitting] = useState(false);

  const fetchReferrals = async () => {
    try {
      const res = await api.get(`${API}/admin/referrals`);
      setReferrals(res.data.referrals);
      setStats(res.data.stats);
    } catch (err) {
      console.error("Failed to fetch referrals:", err);
      toast.error("Failed to load referrals");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReferrals();
  }, []);

  const handleAddReferral = async (e) => {
    e.preventDefault();
    if (!newReferral.referrer_name || !newReferral.referrer_phone) {
      toast.error("Name and phone are required");
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await api.post(`${API}/admin/referrals`, newReferral);
      toast.success(`Referral code created: ${res.data.referral_code}`);
      setShowAddModal(false);
      setNewReferral({ referrer_name: "", referrer_email: "", referrer_phone: "", referrer_vehicle: "", reward_amount: 100 });
      fetchReferrals();
    } catch (err) {
      toast.error("Failed to create referral code");
    } finally {
      setSubmitting(false);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success("Code copied to clipboard!");
  };

  const deactivateReferral = async (id) => {
    if (!window.confirm("Deactivate this referral code?")) return;
    try {
      await api.delete(`${API}/admin/referrals/${id}`);
      toast.success("Referral deactivated");
      fetchReferrals();
    } catch (err) {
      toast.error("Failed to deactivate referral");
    }
  };

  const markPaid = async (id, amount) => {
    if (!window.confirm(`Mark $${amount} as paid out?`)) return;
    try {
      await api.post(`${API}/admin/referrals/${id}/payout`, null, {
        params: { amount }
      });
      toast.success("Payout recorded");
      fetchReferrals();
    } catch (err) {
      toast.error("Failed to record payout");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 size={24} className="animate-spin text-zinc-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="referrals-section">
      {/* Stats Bar */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="border p-4 text-center" style={{ backgroundColor: 'var(--dash-card)', borderColor: 'var(--dash-border)' }}>
          <p className="font-heading text-2xl" style={{ color: 'var(--dash-text)' }}>{stats.total_codes}</p>
          <p className="text-xs uppercase tracking-wider" style={{ color: 'var(--dash-text-dim)' }}>Total Codes</p>
        </div>
        <div className="border p-4 text-center" style={{ backgroundColor: 'var(--dash-card)', borderColor: 'var(--dash-border)' }}>
          <p className="font-heading text-2xl text-[#22c55e]">{stats.active_codes}</p>
          <p className="text-xs uppercase tracking-wider" style={{ color: 'var(--dash-text-dim)' }}>Active</p>
        </div>
        <div className="border p-4 text-center" style={{ backgroundColor: 'var(--dash-card)', borderColor: 'var(--dash-border)' }}>
          <p className="font-heading text-2xl text-[#3b82f6]">{stats.total_referrals}</p>
          <p className="text-xs uppercase tracking-wider" style={{ color: 'var(--dash-text-dim)' }}>Referrals</p>
        </div>
        <div className="border p-4 text-center" style={{ backgroundColor: 'var(--dash-card)', borderColor: 'var(--dash-border)' }}>
          <p className="font-heading text-2xl text-[#f59e0b]">${stats.total_earned}</p>
          <p className="text-xs uppercase tracking-wider" style={{ color: 'var(--dash-text-dim)' }}>Earned</p>
        </div>
        <div className="border p-4 text-center" style={{ backgroundColor: 'var(--dash-card)', borderColor: 'var(--dash-border)' }}>
          <p className="font-heading text-2xl text-red-400">${stats.pending_payouts}</p>
          <p className="text-xs uppercase tracking-wider" style={{ color: 'var(--dash-text-dim)' }}>Pending</p>
        </div>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-lg uppercase tracking-wide" style={{ color: 'var(--dash-text)' }}>Referral Codes</h3>
        <button
          onClick={() => setShowAddModal(true)}
          data-testid="add-referral-btn"
          className="flex items-center gap-2 px-4 py-2 text-xs tracking-[0.1em] uppercase font-body border border-[#22c55e]/30 text-[#22c55e] hover:bg-[#22c55e]/10 transition-colors bg-transparent cursor-pointer"
        >
          <Plus size={14} /> Add Referral
        </button>
      </div>

      {/* Referrals List */}
      {referrals.length === 0 ? (
        <div className="border p-12 text-center" style={{ backgroundColor: 'var(--dash-card)', borderColor: 'var(--dash-border)' }}>
          <Gift size={32} className="mx-auto mb-4" style={{ color: 'var(--dash-text-dim)' }} />
          <p className="font-body" style={{ color: 'var(--dash-text-dim)' }}>No referral codes yet</p>
          <p className="text-sm font-body mt-2" style={{ color: 'var(--dash-text-dim)' }}>Create one for a customer who's completed a job</p>
        </div>
      ) : (
        <div className="space-y-2">
          {referrals.map((ref) => {
            const isExpanded = expandedReferral === ref.id;
            const pendingPayout = (ref.total_earned || 0) - (ref.paid_amount || 0);
            
            return (
              <div key={ref.id} className="border" style={{ backgroundColor: 'var(--dash-card)', borderColor: 'var(--dash-border)' }} data-testid={`referral-${ref.id}`}>
                {/* Main Row */}
                <div 
                  className="p-4 cursor-pointer transition-colors"
                  onClick={() => setExpandedReferral(isExpanded ? null : ref.id)}
                >
                  <div className="flex items-center gap-4">
                    {/* Code */}
                    <div className="flex items-center gap-2">
                      <div className={`w-10 h-10 flex items-center justify-center ${ref.status === "active" ? "bg-[#22c55e]/10" : "bg-zinc-800"}`}>
                        <Gift size={18} className={ref.status === "active" ? "text-[#22c55e]" : "text-zinc-600"} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-lg text-white tracking-wider">{ref.referral_code}</span>
                          <button
                            onClick={(e) => { e.stopPropagation(); copyCode(ref.referral_code); }}
                            className="p-1 text-zinc-600 hover:text-white transition-colors bg-transparent border-none cursor-pointer"
                          >
                            <Copy size={12} />
                          </button>
                        </div>
                        <p className="text-sm text-zinc-500 font-body">{ref.referrer_name}</p>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="flex-1 flex items-center gap-6 justify-end">
                      <div className="text-center hidden sm:block">
                        <p className="text-sm text-white">{ref.total_referrals || 0}</p>
                        <p className="text-xs text-zinc-600">Referrals</p>
                      </div>
                      <div className="text-center hidden sm:block">
                        <p className="text-sm text-[#f59e0b]">${ref.total_earned || 0}</p>
                        <p className="text-xs text-zinc-600">Earned</p>
                      </div>
                      {pendingPayout > 0 && (
                        <div className="text-center">
                          <p className="text-sm text-red-400">${pendingPayout}</p>
                          <p className="text-xs text-zinc-600">Pending</p>
                        </div>
                      )}
                      <span className={`px-2 py-1 text-xs uppercase ${
                        ref.status === "active" ? "bg-[#22c55e]/20 text-[#22c55e] border border-[#22c55e]/30" :
                        ref.status === "expired" ? "bg-zinc-800 text-zinc-500 border border-zinc-700" :
                        "bg-[#f59e0b]/20 text-[#f59e0b] border border-[#f59e0b]/30"
                      }`}>
                        {ref.status}
                      </span>
                      {isExpanded ? <ChevronUp size={16} className="text-zinc-500" /> : <ChevronDown size={16} className="text-zinc-500" />}
                    </div>
                  </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="border-t border-white/5 p-4 bg-black/30">
                    {/* Customer Info */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div className="flex items-center gap-2 text-sm text-zinc-400">
                        <Phone size={14} className="text-zinc-600" />
                        {ref.referrer_phone || "No phone"}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-zinc-400">
                        <Mail size={14} className="text-zinc-600" />
                        {ref.referrer_email || "No email"}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-zinc-400">
                        <Car size={14} className="text-zinc-600" />
                        {ref.referrer_vehicle || "No vehicle recorded"}
                      </div>
                    </div>

                    {/* Referred Jobs */}
                    {ref.referred_jobs_details && ref.referred_jobs_details.length > 0 && (
                      <div className="mb-4">
                        <p className="text-xs text-zinc-600 uppercase tracking-wider mb-2">Referred Jobs</p>
                        <div className="space-y-2">
                          {ref.referred_jobs_details.map((job) => (
                            <div key={job.job_id} className="flex items-center justify-between p-3 bg-white/5 border border-white/5">
                              <div>
                                <p className="text-sm text-white">{job.customer_name} - {job.vehicle}</p>
                                <p className="text-xs text-zinc-500">
                                  Applied: {new Date(job.applied_at).toLocaleDateString("en-AU")}
                                </p>
                              </div>
                              <span className={`px-2 py-1 text-xs uppercase ${
                                job.status === "completed" || job.status === "paid" ? "bg-[#22c55e]/20 text-[#22c55e]" :
                                "bg-[#f59e0b]/20 text-[#f59e0b]"
                              }`}>
                                {job.status === "paid" ? "Paid" : job.status === "completed" ? "Eligible" : "Pending"}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center gap-3 pt-2 border-t border-white/5">
                      {pendingPayout > 0 && (
                        <button
                          onClick={() => markPaid(ref.id, pendingPayout)}
                          className="flex items-center gap-2 px-3 py-1.5 text-xs uppercase tracking-wider bg-[#22c55e]/10 border border-[#22c55e]/30 text-[#22c55e] hover:bg-[#22c55e]/20 transition-colors cursor-pointer"
                        >
                          <Fuel size={12} /> Mark ${pendingPayout} Paid
                        </button>
                      )}
                      {ref.status === "active" && (
                        <button
                          onClick={() => deactivateReferral(ref.id)}
                          className="flex items-center gap-2 px-3 py-1.5 text-xs uppercase tracking-wider bg-transparent border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-colors cursor-pointer"
                        >
                          <X size={12} /> Deactivate
                        </button>
                      )}
                      <span className="text-xs text-zinc-700 ml-auto">
                        Created: {new Date(ref.created_at).toLocaleDateString("en-AU")}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add Referral Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80" onClick={() => setShowAddModal(false)}>
          <div className="bg-[#0a0a0a] border border-white/10 w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <h3 className="font-heading text-lg uppercase tracking-wide text-white mb-6">Create Referral Code</h3>
            
            <form onSubmit={handleAddReferral} className="space-y-4">
              <div>
                <label className="text-xs text-zinc-600 uppercase tracking-wider mb-1 block">Customer Name *</label>
                <input
                  type="text"
                  value={newReferral.referrer_name}
                  onChange={e => setNewReferral({ ...newReferral, referrer_name: e.target.value })}
                  placeholder="John Smith"
                  className="w-full bg-transparent border border-white/10 p-3 text-white font-body placeholder-zinc-600 outline-none focus:border-[#22c55e]/50"
                  required
                />
              </div>
              
              <div>
                <label className="text-xs text-zinc-600 uppercase tracking-wider mb-1 block">Phone *</label>
                <input
                  type="tel"
                  value={newReferral.referrer_phone}
                  onChange={e => setNewReferral({ ...newReferral, referrer_phone: e.target.value })}
                  placeholder="0450 123 456"
                  className="w-full bg-transparent border border-white/10 p-3 text-white font-body placeholder-zinc-600 outline-none focus:border-[#22c55e]/50"
                  required
                />
              </div>
              
              <div>
                <label className="text-xs text-zinc-600 uppercase tracking-wider mb-1 block">Email</label>
                <input
                  type="email"
                  value={newReferral.referrer_email}
                  onChange={e => setNewReferral({ ...newReferral, referrer_email: e.target.value })}
                  placeholder="john@email.com"
                  className="w-full bg-transparent border border-white/10 p-3 text-white font-body placeholder-zinc-600 outline-none focus:border-[#22c55e]/50"
                />
              </div>
              
              <div>
                <label className="text-xs text-zinc-600 uppercase tracking-wider mb-1 block">Their Vehicle (for fuel estimation)</label>
                <input
                  type="text"
                  value={newReferral.referrer_vehicle}
                  onChange={e => setNewReferral({ ...newReferral, referrer_vehicle: e.target.value })}
                  placeholder="2020 Toyota Supra"
                  className="w-full bg-transparent border border-white/10 p-3 text-white font-body placeholder-zinc-600 outline-none focus:border-[#22c55e]/50"
                />
              </div>
              
              <div>
                <label className="text-xs text-zinc-600 uppercase tracking-wider mb-1 block">Reward Amount ($)</label>
                <input
                  type="number"
                  value={newReferral.reward_amount}
                  onChange={e => setNewReferral({ ...newReferral, reward_amount: parseFloat(e.target.value) || 100 })}
                  className="w-full bg-transparent border border-white/10 p-3 text-white font-body placeholder-zinc-600 outline-none focus:border-[#22c55e]/50"
                />
                <p className="text-xs text-zinc-600 mt-1">Internal tracking value (customer sees "free tank of fuel")</p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 px-4 py-3 text-xs uppercase tracking-wider bg-transparent border border-white/10 text-zinc-400 hover:text-white transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 px-4 py-3 text-xs uppercase tracking-wider bg-[#22c55e] text-white font-heading hover:bg-[#22c55e]/80 transition-colors cursor-pointer border-none disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {submitting ? <Loader2 size={14} className="animate-spin" /> : <Gift size={14} />}
                  Create Code
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
